import * as React from "react";
import * as ReactDOM from "react-dom";
import { AppContainer } from "react-hot-loader";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import { applyMiddleware, createStore, Store, StoreEnhancer } from "redux";
import { composeWithDevTools } from "redux-devtools-extension/developmentOnly";
import "regenerator-runtime/runtime";
import { ILanguageOptions, Target } from "../api/api";
import "../ui/_assets/fonts";
import { Desktop, Mobile } from "./containers";
import { ThemeProvider } from "./containers/Theme";
import { i18n } from "./i18n/i18n";
import { rootReducer } from "./redux";
import { notifyStateChange } from "./redux/middlewares";
import * as Utils from "./utils/console";
import { IOptions } from "./utils/options";

Utils.hello();

const initialState: any = (window as any).__INITIAL_STATE__ || {};

export const store: Store<any> = createStore(
  rootReducer,
  initialState,
  composeWithDevTools(applyMiddleware(notifyStateChange)) as StoreEnhancer<any>
);

export let keypadContainerElement: HTMLElement = null;
export let dialogContainerElement: HTMLElement = null;

interface IStaticContext {
  language?: ILanguageOptions;
}

const StaticContext = React.createContext<IStaticContext>({});

// proxy React.useStateContext method so it can be mocked by Jest
export const useStaticContext = () => React.useContext(StaticContext);

const RootContainer = ({ target, language }) => {
  let Route;

  switch (target) {
    case Target.DESKTOP:
      Route = Desktop;
      break;
    case Target.MOBILE:
      Route = Mobile;
      break;
    default:
      return null;
  }

  i18n.addResourceBundle("default", "keypad", language);
  i18n.changeLanguage("default");

  const initStaticContext: IStaticContext = {
    language
  };

  return (
    <AppContainer>
      <I18nextProvider i18n={i18n}>
        <Provider store={store}>
          <ThemeProvider>
            <StaticContext.Provider value={initStaticContext}>
              <Route />
            </StaticContext.Provider>
          </ThemeProvider>
        </Provider>
      </I18nextProvider>
    </AppContainer>
  );
};

export const Core = (options: IOptions) => {
  const { containers, target, language } = options;
  const rootContainer = <RootContainer target={target} language={language} />;

  if (!containers) {
    throw new Error(
      "PlayUI: options.containers is not present in the configuration and it is required."
    );
  }

  if (!(containers && containers.keypad && "nodeType" in containers.keypad)) {
    throw new Error(
      "PlayUI: options.containers.keypad is not a valid DOM-element"
    );
  } else {
    // The keypad's container should not get any event
    // by default, all interactable element should explicitly
    // set pointer-events to "all" in order to get them
    containers.keypad.style.pointerEvents = "none";
    keypadContainerElement = containers.keypad;
  }

  if (!(containers && containers.dialog && "nodeType" in containers.dialog)) {
    throw new Error(
      "PlayUI: options.containers.dialog is not a valid DOM-element"
    );
  } else {
    dialogContainerElement = containers.dialog;
    dialogContainerElement.style.pointerEvents = "none";
  }

  if (rootContainer === null) {
    throw new Error("PlayUI: options.target is not a valid Target");
  }

  const render = () => ReactDOM.render(rootContainer, containers.keypad);

  // Setup hot module replacement
  if (process.env.__DEV__ && module && module.hot) {
    module.hot.accept("./containers/index", () =>
      setImmediate(() => {
        ReactDOM.unmountComponentAtNode(containers.keypad);
        render();
      })
    );
  }

  render();
};
